import { Injectable } from '@angular/core';

import { JobStatusEnum } from '@reach/interfaces/jobs/job-status.enum';
import { GetJobsResponse } from '@reach/interfaces/jobs/jobs.responses';
import {
	GetRelatedNodeCountsRequest,
	UpdateNodeCategoriesRequest,
} from '@reach/interfaces/nodes/nodes.requests';
import {
	GetNodeChunksResponse,
	GetNodeContentResponse,
	GetNodeRelatedNodesResponse,
	GetNodeResponse,
	GetRelatedNodeCountsResponse,
	LearnNodesResponse,
	UpdateNodeCategoriesResponse,
} from '@reach/interfaces/nodes/nodes.responses';
import { CreateNoteResponse } from '@reach/interfaces/notes/notes.responses';
import { SearchNodeResponse } from '@reach/interfaces/search/search-query.type';
import { BooleanEnum } from '@reach/interfaces/typescript/boolean-enum.type';

import { BaseApiService } from '~app-client/api/_internal';
import { ReachNodeId } from '~app-client/core/types';
import { WithWorkspaceIds, withWorkspaceIds } from '~app-client/workspaces/types';

@Injectable({ providedIn: 'root' })
export class NodesApiService extends BaseApiService {
	protected commonPath = ['nodes'];

	public async getRelatedNodeCounts(
		body: GetRelatedNodeCountsRequest
	): Promise<GetRelatedNodeCountsResponse> {
		return await this.http.post<GetRelatedNodeCountsResponse>(
			this.url('get-related-node-counts'),
			body
		);
	}

	public async getMany(
		ids: ReachNodeId[]
	): Promise<Array<WithWorkspaceIds<SearchNodeResponse>>> {
		if (ids.length === 0) {
			return [];
		}

		const body = {
			nodeIds: ids,
		};
		const nodes = await this.http.post<SearchNodeResponse[]>(this.url('get-nodes'), body);
		return nodes.map((node) => withWorkspaceIds(node));
	}

	public async getNodeInfo(
		nodeId: ReachNodeId
	): Promise<WithWorkspaceIds<GetNodeResponse>> {
		const params = {
			t: this.paramTime,
		};
		const node = await this.http.get<GetNodeResponse>(this.url(nodeId), params);
		return withWorkspaceIds(node);
	}

	public async getRelated(
		nodeId: ReachNodeId,
		cursor: string | undefined,
		size: number
	): Promise<GetNodeRelatedNodesResponse> {
		const params: Record<string, string> = {
			size: `${size}`,
			...(cursor !== undefined ? { cursor } : {}),
		};

		return await this.http.get<GetNodeRelatedNodesResponse>(
			this.url(nodeId, 'related-nodes'),
			params
		);
	}

	public async getJobs(nodeId: ReachNodeId): Promise<GetJobsResponse> {
		const params = {
			t: this.paramTime,
			status: [JobStatusEnum.IN_PROGRESS, JobStatusEnum.PENDING],
			returnLastJobs: BooleanEnum.YES,
		};

		return await this.http.get<GetJobsResponse>(this.url(nodeId, 'jobs'), params);
	}

	public async changeWikipediaPage(
		nodeId: ReachNodeId,
		body: { wikipediaUrl?: string; description?: string; imageUrl?: string }
	): Promise<WithWorkspaceIds<GetNodeResponse>> {
		const node = await this.http.post<GetNodeResponse>(this.url(nodeId, 'wikipedia'), body);
		return withWorkspaceIds(node);
	}

	public async getContent(nodeId: ReachNodeId): Promise<GetNodeContentResponse> {
		const query = {
			t: `${this.cacheTimer}`,
		};
		return await this.http.get<GetNodeContentResponse>(this.url(nodeId, 'content'), query);
	}

	public async getChunks(nodeId: ReachNodeId): Promise<GetNodeChunksResponse> {
		return await this.http.get<GetNodeChunksResponse>(this.url(nodeId, 'chunks'));
	}

	public async editChunks(
		nodeId: ReachNodeId,
		body: { chunks: GetNodeChunksResponse }
	): Promise<GetNodeChunksResponse> {
		return await this.http.patch<GetNodeChunksResponse>(this.url(nodeId, 'chunks'), body);
	}

	public async getSummary(nodeId: ReachNodeId): Promise<{
		summary: string;
		isEmpty: boolean;
		isLoading: boolean;
	}> {
		const query = {
			t: this.paramTime,
		};
		return await this.http.get<{
			summary: string;
			isEmpty: boolean;
			isLoading: boolean;
		}>(this.url(nodeId, 'summary'), query);
	}

	public async convertContentToNote(nodeId: ReachNodeId): Promise<CreateNoteResponse> {
		return await this.http.post<CreateNoteResponse>(this.url(nodeId, 'to-note'));
	}

	public async processSummaryAndKeyphrases(
		nodesIds: ReachNodeId[]
	): Promise<Array<{ jobId: string; nodeId: ReachNodeId }>> {
		const body = {
			nodeIds: nodesIds,
		};
		return await this.http.post<Array<{ jobId: string; nodeId: ReachNodeId }>>(
			this.url('process-summary-keyphrases'),
			body
		);
	}

	public async learnNodes(nodesIds: ReachNodeId[]): Promise<LearnNodesResponse> {
		const body = {
			nodeIds: nodesIds,
		};
		return await this.http.post<LearnNodesResponse>(this.url('learn'), body);
	}

	public async learnAll(): Promise<LearnNodesResponse> {
		return await this.http.post<LearnNodesResponse>(this.url('learn-all'));
	}

	public async updateContent(nodeId: ReachNodeId, content: string): Promise<void> {
		return await this.http.put<void>(this.url(nodeId, 'content'), { content });
	}

	public async updateNodeCategories(
		nodeIds: ReachNodeId[],
		changes: Pick<UpdateNodeCategoriesRequest, 'add' | 'remove'>
	): Promise<UpdateNodeCategoriesResponse> {
		const body: UpdateNodeCategoriesRequest = { nodeIds, ...changes };
		return await this.http.post<UpdateNodeCategoriesResponse>(
			this.url('node-categories'),
			body
		);
	}
}
